/**
 * different libraries send different seriesVisibility.
 * roi-charts send Record<string, number>, example: {chart-0-0: 1}
 * echarts send Record<string, boolean>, example: {2015: false, 1990: true}
 */
export class SessionStorageService {
    constructor() {}
    public setSeriesVisibility(
        uniqueId: string,
        viewId: number,
        chartlib: string,
        seriesVisibility: unknown,
    ) {
        const storedPortletInfo = sessionStorage.getItem(uniqueId);
        const currentValue = storedPortletInfo ? JSON.parse(storedPortletInfo) : {};

        /**
         * in tabulator we need to store expanded rows and collapsed ones.
         * there is a feature dataTreeStartExpanded which could mess thing up.
         * for example, table start expanded, but user want to hide some rows - we need to store it.
         * {
         * "seriesVisibility": {
         *      "1": {
         *          "expandedRows": [...ids],
         *          "collapsedRows": [...ids]
         *      }
         *    }
         * }
         *
         */
        if (chartlib === 'tabulator') {
            sessionStorage.setItem(
                uniqueId,
                JSON.stringify({
                    ...currentValue,
                    seriesVisibility: {
                        ...currentValue.seriesVisibility,
                        [viewId]: seriesVisibility,
                    },
                }),
            );
        }
        /**
         * echarts are much simplier than roicharts.
         * there we can always overwrite whole value, because
         * event from chart have boolean values for each series
         * {
         * "seriesVisibility": {
         *      "1": {
         *          "1990": true,
         *          "2015": false
         *      }
         *    }
         * }
         *
         */
        if (chartlib === 'echarts') {
            sessionStorage.setItem(
                uniqueId,
                JSON.stringify({
                    ...currentValue,
                    seriesVisibility: {
                        ...currentValue.seriesVisibility,
                        [viewId]: seriesVisibility,
                    },
                }),
            );
        }

        if (chartlib === 'roi-charts') {
            /**
             * roi-charts could have multiple views, with multiple charts on one portlet.
             * we have to store information about portlet uniqueId -> viewId -> chartId ->
             * -> series to filter out.
             * {
             * "seriesVisibility": {
             *    "1": {
             *      "chart-0-0": [
             *          9,
             *          10,
             *          8
             *      ]
             *      },
             *      "3": {
             *        "chart-0-0": [
             *          9,
             *          10
             *      ]
             *      }
             *  }
             * }
             */

            // getting charts series visibility for current view
            const seriesVisibilityForView = currentValue.seriesVisibility?.[viewId] || {};

            Object.entries(seriesVisibility as Record<string, number>).forEach(
                ([chartId, index]) => {
                    const visibilityForChart = seriesVisibilityForView[chartId] || [];

                    const indexInArray = visibilityForChart.indexOf(index);
                    const updatedVisibilityForChart =
                        indexInArray > -1
                            ? visibilityForChart.filter((item: number) => item !== index)
                            : [...visibilityForChart, index];

                    seriesVisibilityForView[chartId] = updatedVisibilityForChart;
                },
            );

            sessionStorage.setItem(
                uniqueId,
                JSON.stringify({
                    ...currentValue,
                    seriesVisibility: {
                        ...currentValue.seriesVisibility,
                        [viewId]: seriesVisibilityForView,
                    },
                }),
            );
        }
    }

    public getSeriesVisibility(uniqueId: string, viewId: number): unknown | null {
        const value = sessionStorage.getItem(uniqueId);
        const parsedValue = value && JSON.parse(value).seriesVisibility;

        const visibilityForViewId = parsedValue?.[viewId] || null;
        return visibilityForViewId;
    }

    public setSelectedItem(
        uniqueId: string,
        viewId: number,
        chartlib: string,
        selectedItem: unknown,
    ) {
        const storedPortletInfo = sessionStorage.getItem(uniqueId);
        const currentValue = storedPortletInfo ? JSON.parse(storedPortletInfo) : {};
        const currentSeriesVisibility = currentValue.seriesVisibility?.[viewId] || {};
        // for now we are storing only tabulator info, for scrollTo feature
        if (chartlib === 'tabulator') {
            sessionStorage.setItem(
                uniqueId,
                JSON.stringify({
                    ...currentValue,
                    seriesVisibility: {
                        ...currentValue.seriesVisibility,
                        [viewId]: {
                            ...currentSeriesVisibility,
                            lastSelectedItem: selectedItem,
                        },
                    },
                }),
            );
        }
    }
}
